import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api/client";
import { Spinner, EmptyState } from "@/components/ui";
import Modal from "@/components/ui/Modal";
import toast from "react-hot-toast";
import clsx from "clsx";
import { buildInviteLink } from "@/lib/studox-auth/invite.js";

/* ─── Role config ──────────────────────────────────────────────────────────── */
const ROLES = [
  { id: "student", label: "Schüler",   color: "#8b5cf6", bg: "rgba(139,92,246,0.15)",
    Icon: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5-10-5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg> },
  { id: "teacher", label: "Lehrperson", color: "#06b6d4", bg: "rgba(6,182,212,0.15)",
    Icon: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg> },
  { id: "mentor",  label: "Mentor",     color: "#22c55e", bg: "rgba(34,197,94,0.15)",
    Icon: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
  { id: "parent",  label: "Elternteil", color: "#fbbf24", bg: "rgba(251,191,36,0.15)",
    Icon: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg> },
];
const ROLE_MAP = Object.fromEntries(ROLES.map((r) => [r.id, r]));

/* ─── Institution type labels ─────────────────────────────────────────────── */
const TYPE_LABELS = { school: "Schule", center: "Förderzentrum", youth: "Jugendzentrum", therapy: "Therapiezentrum" };
const TYPE_COLORS = { school: "#8b5cf6", center: "#06b6d4", youth: "#22c55e", therapy: "#f59e0b" };

/* ─── Copy icon ─────────────────────────────────────────────────────────────── */
const CopyIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
);

/* ─── Stat card ─────────────────────────────────────────────────────────────── */
function StatCard({ role, total, used, free }) {
  const pct = total > 0 ? Math.round((used / total) * 100) : 0;
  return (
    <div className="bg-nexus-sf border border-nexus-border rounded-2xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: role.bg, color: role.color }}>
            <role.Icon/>
          </div>
          <span className="text-sm font-bold text-gray-300">{role.label}</span>
        </div>
        <div className="text-right">
          <span className="text-lg font-black" style={{ color: role.color }}>{free}</span>
          <p className="text-[9px] text-gray-600 font-medium">verfügbar</p>
        </div>
      </div>
      {/* Usage bar */}
      <div>
        <div className="flex justify-between items-center mb-1.5">
          <span className="text-[10px] text-gray-600">Nutzungsrate</span>
          <span className="text-[10px] font-bold" style={{ color: role.color }}>{pct}%</span>
        </div>
        <div className="h-1.5 bg-nexus-s2 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${role.color}88, ${role.color})` }}/>
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-[9px] text-gray-700">{used} genutzt</span>
          <span className="text-[9px] text-gray-700">{total} gesamt</span>
        </div>
      </div>
    </div>
  );
}

/* ─── Code row ──────────────────────────────────────────────────────────────── */
function CodeRow({ c, onCopy, onCopyInvite, onDelete }) {
  const role = ROLE_MAP[c.role];
  const typeColor = c.institutionId ? (TYPE_COLORS[c.institutionType] || "#6b7280") : null;
  return (
    <div className="group flex items-center gap-3 p-3 rounded-xl bg-nexus-sf border border-nexus-border hover:border-violet-500/25 hover:bg-nexus-s2 transition-all duration-200">
      {/* Role badge */}
      <div className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: role?.bg, color: role?.color }}>
        <role.Icon/>
      </div>

      {/* Code + meta */}
      <div className="flex-1 min-w-0">
        <button
          onClick={() => onCopy(c.code)}
          className="font-mono font-black text-sm text-violet-300 hover:text-violet-200 transition-colors flex items-center gap-1.5 group/copy"
        >
          <span className="tracking-widest">{c.code}</span>
          <span className="opacity-0 group-hover/copy:opacity-60 transition-opacity"><CopyIcon/></span>
        </button>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
          <span className="text-[10px] font-bold" style={{ color: role?.color }}>{role?.label}</span>
          {c.allowedApps && <AppChips apps={c.allowedApps} />}
          {c.institutionName && (
            <>
              <span className="text-[9px] text-gray-700">·</span>
              <span className="text-[10px] font-medium flex items-center gap-0.5" style={{ color: typeColor || "#9ca3af" }}>
                <svg width="8" height="8" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
                {c.institutionName}
              </span>
            </>
          )}
          {c.note && <span className="text-[10px] text-gray-600 truncate max-w-[100px]">— {c.note}</span>}
        </div>
      </div>

      {/* Date */}
      <span className="text-[10px] text-gray-700 flex-shrink-0 hidden sm:block tabular-nums">
        {new Date(c.createdAt).toLocaleDateString("de-DE", { day: "2-digit", month: "short" })}
      </span>

      {/* Copy invite link */}
      <button
        onClick={() => onCopyInvite(c.code)}
        title="Einladungslink kopieren"
        className="opacity-0 group-hover:opacity-100 flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-violet-400 hover:text-violet-300 hover:bg-violet-500/10 transition-all"
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" strokeWidth="2.2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
        </svg>
      </button>

      {/* Delete */}
      <button
        onClick={() => onDelete(c.id)}
        className="opacity-0 group-hover:opacity-100 flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-red-500 hover:text-red-400 hover:bg-red-500/10 transition-all"
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" strokeWidth="2.2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
        </svg>
      </button>
    </div>
  );
}

/* ─── Used code row ─────────────────────────────────────────────────────────── */
function UsedCodeRow({ c }) {
  const role = ROLE_MAP[c.role];
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-nexus-sf/40 border border-nexus-border/40 opacity-50">
      <div className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: role?.bg, color: role?.color }}>
        <role.Icon/>
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-mono font-black text-sm text-gray-600 line-through tracking-widest">{c.code}</p>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className="text-[10px] text-gray-600">{role?.label}</span>
          {c.institutionName && <><span className="text-[9px] text-gray-700">·</span><span className="text-[10px] text-gray-600">{c.institutionName}</span></>}
          {c.usedBy && <><span className="text-[9px] text-gray-700">→</span><span className="text-[10px] text-gray-500 font-medium">{c.usedBy.name}</span></>}
        </div>
      </div>
      <div className="flex-shrink-0 flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/10 border border-green-500/20">
        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="#22c55e" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        <span className="text-[9px] text-green-400 font-bold">Genutzt</span>
      </div>
    </div>
  );
}

/* ─── Institution selector in modal ─────────────────────────────────────────── */
function InstitutionSelector({ value, onChange, institutions }) {
  const [open, setOpen] = useState(false);
  const selected = institutions.find((i) => i.id === value);

  // Group by type
  const groups = {};
  institutions.forEach((i) => {
    if (!groups[i.type]) groups[i.type] = [];
    groups[i.type].push(i);
  });

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm transition-all duration-200 text-left"
        style={{
          background: "rgba(255,255,255,0.04)",
          borderColor: open ? "rgba(139,92,246,0.5)" : "rgba(255,255,255,0.07)",
          color: selected ? "#fff" : "#6b7280",
        }}
      >
        {selected ? (
          <>
            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: TYPE_COLORS[selected.type] || "#8b5cf6" }}/>
            <span className="flex-1 font-medium truncate">{selected.name}</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded-md font-bold flex-shrink-0" style={{ background: "rgba(139,92,246,0.15)", color: TYPE_COLORS[selected.type] || "#8b5cf6" }}>
              {TYPE_LABELS[selected.type] || selected.type}
            </span>
          </>
        ) : (
          <span className="flex-1">Einrichtung wählen (optional)</span>
        )}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"
          style={{ color: "#6b7280", transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s", flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full mt-1.5 rounded-2xl border overflow-hidden z-50 max-h-52 overflow-y-auto scrollbar-hide animate-fade-in"
          style={{ background: "#0f0f1e", borderColor: "rgba(139,92,246,0.3)", boxShadow: "0 20px 60px rgba(0,0,0,0.7)" }}>
          {/* No institution option */}
          <button
            type="button"
            onClick={() => { onChange(""); setOpen(false); }}
            className="w-full px-3 py-2.5 text-left text-xs font-medium transition-colors hover:bg-white/5"
            style={{ color: value === "" ? "#8b5cf6" : "#6b7280" }}
          >
            Keine Einrichtung zuweisen
          </button>
          <div className="h-px bg-white/5"/>

          {Object.entries(groups).map(([type, items]) => (
            <div key={type}>
              <div className="px-3 py-1.5 text-[9px] font-black uppercase tracking-widest" style={{ color: TYPE_COLORS[type] || "#8b5cf6", background: "rgba(255,255,255,0.02)" }}>
                {TYPE_LABELS[type] || type}
              </div>
              {items.map((inst) => (
                <button
                  key={inst.id}
                  type="button"
                  onClick={() => { onChange(inst.id); setOpen(false); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-sm transition-colors hover:bg-white/5"
                  style={{ color: value === inst.id ? "#fff" : "#9ca3af" }}
                >
                  <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: TYPE_COLORS[type] || "#8b5cf6", opacity: value === inst.id ? 1 : 0.4 }}/>
                  <span className="flex-1 font-medium truncate">{inst.name}</span>
                  {inst.city && <span className="text-[10px] text-gray-600 flex-shrink-0">{inst.city}</span>}
                  {value === inst.id && (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" strokeWidth="3" stroke="#8b5cf6" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  )}
                </button>
              ))}
            </div>
          ))}

          {Object.keys(groups).length === 0 && (
            <p className="px-3 py-4 text-center text-xs text-gray-600">Noch keine Einrichtungen angelegt</p>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── App chips ─────────────────────────────────────────────────────────────── */
const APP_CHIPS = {
  flow:  { label: "Flow",          color: "#8b5cf6", icon: "🎓" },
  clubs: { label: "Offline Clubs", color: "#10b981", icon: "🏛️" },
  step:  { label: "Step",          color: "#f97316", icon: "🪜" },
};

function AppChips({ apps }) {
  let parsed = [];
  try { parsed = JSON.parse(apps || '["flow"]'); } catch { parsed = ["flow"]; }
  return (
    <div className="flex gap-1">
      {parsed.map((app) => {
        const info = APP_CHIPS[app] || { label: app, color: "#6b7280", icon: "📱" };
        return (
          <span key={app} className="text-[9px] px-1.5 py-0.5 rounded-full font-bold" style={{ background: `${info.color}20`, color: info.color }}>
            {info.icon} {info.label}
          </span>
        );
      })}
    </div>
  );
}

/* ─── Main component ─────────────────────────────────────────────────────────── */
export default function CodeManager() {
  const qc = useQueryClient();
  const [genModal,      setGenModal]     = useState(false);
  const [role,          setRole]         = useState("student");
  const [note,          setNote]         = useState("");
  const [institutionId, setInstitutionId] = useState("");
  const [count,         setCount]        = useState(1);
  const [allowedApps,   setAllowedApps]  = useState(["flow"]);
  const [filterRole,    setFilterRole]   = useState("all");
  const [filterInst,    setFilterInst]   = useState("all");
  const [showUsed,      setShowUsed]     = useState(false);
  const [groupByInst,   setGroupByInst]  = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["codes"],
    queryFn: () => api.get("/codes").then((r) => r.data),
  });

  const { data: instData } = useQuery({
    queryKey: ["institutions-all"],
    queryFn: () => api.get("/institutions").then((r) => r.data),
  });

  const institutions = instData?.institutions || [];

  const generate = useMutation({
    mutationFn: () => api.post("/codes/generate", {
      role, note: note || undefined, count,
      allowedApps,
      institutionId: institutionId || undefined,
    }),
    onSuccess: (res) => {
      const codes = res.data.codes || [res.data.code];
      toast.success(`${codes.length} Code${codes.length > 1 ? "s" : ""} generiert`);
      qc.invalidateQueries({ queryKey: ["codes"] });
      setGenModal(false);
      setNote(""); setInstitutionId(""); setCount(1); setAllowedApps(["flow"]);
    },
    onError: (err) => toast.error(err.response?.data?.error || "Fehler"),
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/codes/${id}`),
    onSuccess: () => { toast.success("Code entfernt"); qc.invalidateQueries({ queryKey: ["codes"] }); },
    onError: (err) => toast.error(err.response?.data?.error || "Fehler"),
  });

  const copyCode = (code) => { navigator.clipboard.writeText(code).catch(() => {}); toast.success(`Kopiert: ${code}`); };
  const copyInvite = (code) => { navigator.clipboard.writeText(buildInviteLink("https://core.studox.eu", code)).catch(() => {}); toast.success("Einladungslink kopiert 🎟️"); };

  const codes   = data?.codes || [];
  const unused  = codes.filter((c) => !c.usedById);
  const used    = codes.filter((c) =>  c.usedById);

  // Apply filters
  const applyFilters = (list) => {
    let r = list;
    if (filterRole !== "all")  r = r.filter((c) => c.role === filterRole);
    if (filterInst !== "all")  r = r.filter((c) => (filterInst === "__none__" ? !c.institutionId : c.institutionId === filterInst));
    return r;
  };

  const filteredUnused = applyFilters(unused);
  const filteredUsed   = applyFilters(used);

  /* Per-role stats */
  const stats = ROLES.map((r) => ({
    ...r,
    total: codes.filter((c) => c.role === r.id).length,
    used:  codes.filter((c) => c.role === r.id && !!c.usedById).length,
    free:  codes.filter((c) => c.role === r.id && !c.usedById).length,
  }));

  /* Group unused by institution */
  const groupedUnused = (() => {
    if (!groupByInst) return null;
    const groups = {};
    filteredUnused.forEach((c) => {
      const key = c.institutionId || "__none__";
      const label = c.institutionName || "Ohne Einrichtung";
      if (!groups[key]) groups[key] = { label, codes: [] };
      groups[key].codes.push(c);
    });
    return Object.values(groups);
  })();

  /* Unique institutions with codes for filter */
  const institutionsWithCodes = institutions.filter((i) => codes.some((c) => c.institutionId === i.id));

  return (
    <div className="space-y-5 animate-fade-in">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-black flex items-center gap-2 text-white">
            <span className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: "rgba(139,92,246,0.15)" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="#8b5cf6" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </span>
            Zugangscodes
          </h1>
          <p className="text-xs text-gray-500 mt-0.5 ml-11">
            <span className="text-green-400 font-bold">{unused.length}</span>
            <span className="text-gray-700 mx-1">verfügbar</span>
            <span className="text-gray-700 mx-0.5">·</span>
            <span className="text-gray-600">{used.length} genutzt</span>
            <span className="text-gray-700 mx-0.5">·</span>
            <span className="text-gray-700">{codes.length} gesamt</span>
          </p>
        </div>
        <button
          onClick={() => setGenModal(true)}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-white text-xs font-black transition-all duration-200 flex-shrink-0"
          style={{ background: "linear-gradient(135deg, #7c3aed, #5b21b6)", boxShadow: "0 4px 20px rgba(124,58,237,0.35)" }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 6px 24px rgba(124,58,237,0.5)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(124,58,237,0.35)"; }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Generieren
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center pt-16"><Spinner size="lg"/></div>
      ) : (
        <>
          {/* ── Stats grid ────────────────────────────────────────────────── */}
          <div className="grid grid-cols-2 gap-2.5">
            {stats.map((s) => <StatCard key={s.id} role={s} total={s.total} used={s.used} free={s.free}/>)}
          </div>

          {/* ── Filters bar ───────────────────────────────────────────────── */}
          <div className="space-y-2">
            {/* Role filter */}
            <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-0.5">
              {[{ id: "all", label: "Alle Rollen", color: "#8b5cf6" }, ...ROLES].map((r) => (
                <button
                  key={r.id}
                  onClick={() => setFilterRole(r.id)}
                  className={clsx("flex-shrink-0 px-3 py-1.5 rounded-xl text-[11px] font-bold border transition-all duration-200")}
                  style={filterRole === r.id
                    ? { background: r.color, borderColor: r.color, color: "#fff" }
                    : { background: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.06)", color: "#6b7280" }}
                >
                  {r.id !== "all" ? r.label : r.label}
                  {r.id !== "all" && (
                    <span className={clsx("ml-1 text-[9px]", filterRole === r.id ? "opacity-70" : "text-gray-700")}>
                      {unused.filter((c) => c.role === r.id).length}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Institution filter (only shown if institutions exist) */}
            {institutionsWithCodes.length > 0 && (
              <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-0.5">
                {[
                  { id: "all", label: "Alle Einrichtungen" },
                  { id: "__none__", label: "Ohne Einrichtung" },
                  ...institutionsWithCodes.map((i) => ({ id: i.id, label: i.name, type: i.type })),
                ].map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setFilterInst(f.id)}
                    className="flex-shrink-0 px-3 py-1.5 rounded-xl text-[11px] font-bold border transition-all duration-200"
                    style={filterInst === f.id
                      ? { background: "rgba(139,92,246,0.2)", borderColor: "rgba(139,92,246,0.5)", color: "#c4b5fd" }
                      : { background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.05)", color: "#6b7280" }}
                  >
                    {f.type && <span className="mr-1" style={{ color: TYPE_COLORS[f.type] || "#8b5cf6" }}>●</span>}
                    {f.label}
                  </button>
                ))}
              </div>
            )}

            {/* Group toggle */}
            <div className="flex justify-end">
              <button
                onClick={() => setGroupByInst((v) => !v)}
                className="flex items-center gap-1.5 text-[10px] font-bold transition-colors"
                style={{ color: groupByInst ? "#8b5cf6" : "#4b5563" }}
              >
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
                  <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
                </svg>
                Nach Einrichtung gruppieren
              </button>
            </div>
          </div>

          {/* ── Available codes ───────────────────────────────────────────── */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"/>
              <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest">Verfügbar ({filteredUnused.length})</h3>
            </div>

            {filteredUnused.length === 0 ? (
              <EmptyState
                icon={<svg width="32" height="32" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" stroke="#4b5563" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>}
                title="Keine Codes"
                desc="Generiere neue Zugangscodes"
              />
            ) : groupedUnused ? (
              /* Grouped view */
              <div className="space-y-4">
                {groupedUnused.map((group) => (
                  <div key={group.label}>
                    <div className="flex items-center gap-2 mb-2">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="#6b7280" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
                      <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">{group.label}</span>
                      <span className="text-[9px] text-gray-700 ml-auto">{group.codes.length} Codes</span>
                    </div>
                    <div className="space-y-1.5 pl-2 border-l-2 border-white/5">
                      {group.codes.map((c) => (
                        <CodeRow key={c.id} c={c} onCopy={copyCode} onCopyInvite={copyInvite} onDelete={(id) => del.mutate(id)}/>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* Flat view */
              <div className="space-y-1.5">
                {filteredUnused.map((c) => (
                  <CodeRow key={c.id} c={c} onCopy={copyCode} onCopyInvite={copyInvite} onDelete={(id) => del.mutate(id)}/>
                ))}
              </div>
            )}
          </section>

          {/* ── Used codes ────────────────────────────────────────────────── */}
          {filteredUsed.length > 0 && (
            <section>
              <button
                onClick={() => setShowUsed((v) => !v)}
                className="flex items-center gap-2 text-xs font-black text-gray-600 hover:text-gray-400 uppercase tracking-widest mb-3 transition-colors"
              >
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"
                  className={clsx("transition-transform", showUsed ? "rotate-90" : "")}>
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
                Genutzt ({filteredUsed.length})
              </button>
              {showUsed && (
                <div className="space-y-1.5 animate-fade-in">
                  {filteredUsed.map((c) => <UsedCodeRow key={c.id} c={c}/>)}
                </div>
              )}
            </section>
          )}
        </>
      )}

      {/* ── Generate Modal ────────────────────────────────────────────────── */}
      <Modal open={genModal} onClose={() => setGenModal(false)} title="Zugangscode generieren">
        <div className="space-y-4">
          {/* Role selector */}
          <div>
            <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest mb-2.5">Rolle</p>
            <div className="grid grid-cols-2 gap-2">
              {ROLES.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setRole(r.id)}
                  className="flex items-center gap-2.5 p-3 rounded-xl border text-left transition-all duration-200"
                  style={role === r.id
                    ? { background: r.bg, borderColor: r.color, color: r.color }
                    : { background: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.07)", color: "#6b7280" }}
                >
                  <r.Icon/>
                  <span className="text-xs font-bold">{r.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Institution selector */}
          <div>
            <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest mb-2.5">
              Einrichtung <span className="text-gray-700 normal-case font-medium">(optional)</span>
            </p>
            <InstitutionSelector
              value={institutionId}
              onChange={setInstitutionId}
              institutions={institutions}
            />
          </div>

          {/* App access */}
          <div>
            <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest mb-1">App-Zugang</p>
            <p className="text-[10px] text-gray-700 mb-2.5">Welche StudoX-Apps kann dieser Code freischalten?</p>
            <div className="grid grid-cols-1 gap-2">
              {[
                { key: "flow",  label: "StudoX Flow",          icon: "🎓", color: "#8b5cf6", desc: "Gamifizierte Lernplattform für Schulen & Institute" },
                { key: "clubs", label: "StudoX Offline Clubs", icon: "🏛️", color: "#10b981", desc: "Lernclubs, Studysessions & Community-Features" },
                { key: "step",  label: "StudoX Step",          icon: "🪜", color: "#f97316", desc: "Lern-App für Volksschulkinder (bald verfügbar)", disabled: true },
              ].map((app) => {
                const isSelected = allowedApps.includes(app.key);
                return (
                  <button
                    key={app.key}
                    type="button"
                    disabled={app.locked || app.disabled}
                    onClick={() => {
                      if (app.locked || app.disabled) return;
                      setAllowedApps((prev) =>
                        isSelected
                          ? prev.filter((a) => a !== app.key)
                          : [...prev, app.key]
                      );
                    }}
                    className="flex items-center gap-3 p-3 rounded-xl border text-left transition-all duration-200 w-full disabled:cursor-default"
                    style={isSelected
                      ? { background: `${app.color}18`, borderColor: `${app.color}80`, color: app.color }
                      : app.disabled
                        ? { background: "rgba(255,255,255,0.015)", borderColor: "rgba(255,255,255,0.04)", color: "#374151" }
                        : { background: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.07)", color: "#6b7280" }}
                  >
                    <span className="text-xl flex-shrink-0" style={{ opacity: app.disabled ? 0.3 : 1 }}>{app.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-xs font-bold">{app.label}</p>
                        {app.locked && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full font-black"
                            style={{ background: `${app.color}20`, color: app.color }}>immer</span>
                        )}
                        {app.disabled && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full font-black text-gray-700"
                            style={{ background: "rgba(255,255,255,0.04)" }}>bald</span>
                        )}
                      </div>
                      <p className="text-[10px] mt-0.5" style={{ color: isSelected ? `${app.color}80` : "#4b5563" }}>{app.desc}</p>
                    </div>
                    <div
                      className="flex-shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all"
                      style={isSelected
                        ? { background: app.color, borderColor: app.color }
                        : app.disabled
                          ? { background: "transparent", borderColor: "rgba(255,255,255,0.06)" }
                          : { background: "transparent", borderColor: "rgba(255,255,255,0.15)" }}
                    >
                      {isSelected && (
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" strokeWidth="3.5" stroke="#fff" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Count */}
          <div>
            <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest mb-2.5">Anzahl</p>
            <div className="flex gap-2">
              {[1, 5, 10, 20, 30].map((n) => (
                <button
                  key={n}
                  onClick={() => setCount(n)}
                  className="flex-1 py-2 rounded-xl text-xs font-black border transition-all duration-200"
                  style={count === n
                    ? { background: "rgba(139,92,246,0.2)", borderColor: "rgba(139,92,246,0.5)", color: "#c4b5fd" }
                    : { background: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.07)", color: "#6b7280" }}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          {/* Note */}
          <div>
            <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest mb-2.5">
              Notiz <span className="text-gray-700 normal-case font-medium">(optional)</span>
            </p>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="z.B. Klasse 4b – Schuljahr 2025/26"
              className="w-full rounded-xl p-3 text-sm text-white placeholder-gray-600 resize-none h-14 focus:outline-none transition-all duration-200"
              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)" }}
              onFocus={(e) => { e.currentTarget.style.borderColor = "rgba(139,92,246,0.5)"; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)"; }}
            />
          </div>

          <button
            onClick={() => generate.mutate()}
            disabled={generate.isPending}
            className="w-full py-3 rounded-xl text-white text-sm font-black transition-all duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ background: "linear-gradient(135deg, #7c3aed, #5b21b6)", boxShadow: "0 4px 20px rgba(124,58,237,0.3)" }}
          >
            {generate.isPending ? (
              <><div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin"/><span>Generiere…</span></>
            ) : (
              <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>
              {count === 1 ? "Code generieren" : `${count} Codes generieren`}</>
            )}
          </button>
        </div>
      </Modal>
    </div>
  );
}
